import { type Static, Type } from "@sinclair/typebox";
import { Value } from "@sinclair/typebox/value";
import { CoachServiceRecordSchema } from "./coach.js";

const StableIdSchema = Type.String({
  minLength: 1,
  maxLength: 128,
  pattern: "^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$",
});
const IdempotencyKeySchema = Type.String({
  minLength: 8,
  maxLength: 128,
  pattern: "^[A-Za-z0-9][A-Za-z0-9._:-]{7,127}$",
});
const DateTimeSchema = Type.String({
  minLength: 24,
  maxLength: 24,
  pattern: "^[0-9]{4}-[0-9]{2}-[0-9]{2}T[0-9]{2}:[0-9]{2}:[0-9]{2}\\.[0-9]{3}Z$",
});
const Sha256Schema = Type.String({ pattern: "^[a-f0-9]{64}$" });
const ReasonSchema = Type.String({ minLength: 2, maxLength: 500 });

export const CoachServiceCorrectionKindSchema = Type.Union([
  Type.Literal("MISASSIGN_CANCEL"),
  Type.Literal("LATE_START_ADJUST"),
]);
export type CoachServiceCorrectionKind = Static<typeof CoachServiceCorrectionKindSchema>;

export const PreviewCoachServiceCorrectionRequestSchema = Type.Union([
  Type.Object(
    {
      kind: Type.Literal("MISASSIGN_CANCEL"),
      correctedStartedAt: Type.Null(),
      reason: ReasonSchema,
    },
    { additionalProperties: false },
  ),
  Type.Object(
    {
      kind: Type.Literal("LATE_START_ADJUST"),
      correctedStartedAt: DateTimeSchema,
      reason: ReasonSchema,
    },
    { additionalProperties: false },
  ),
]);
export type PreviewCoachServiceCorrectionRequest = Static<
  typeof PreviewCoachServiceCorrectionRequestSchema
>;

export const CoachServiceCorrectionEffectSchema = Type.Object(
  {
    effectiveSeconds: Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER }),
    billedMinutes: Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER }),
    serviceFeeFen: Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER }),
    commissionFen: Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER }),
  },
  { additionalProperties: false },
);
export type CoachServiceCorrectionEffect = Static<typeof CoachServiceCorrectionEffectSchema>;

export const CoachServiceCorrectionPreviewReadModelSchema = Type.Object(
  {
    serviceId: StableIdSchema,
    expectedServiceVersion: Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
    kind: CoachServiceCorrectionKindSchema,
    coachId: StableIdSchema,
    coachDisplayName: Type.String({ minLength: 1, maxLength: 80 }),
    tableNumber: Type.String({ minLength: 1, maxLength: 32 }),
    serviceTypeName: Type.String({ minLength: 1, maxLength: 80 }),
    serviceStatus: Type.Union([Type.Literal("ASSIGNED"), Type.Literal("ACTIVE")]),
    assignedAt: DateTimeSchema,
    originalStartedAt: Type.Union([DateTimeSchema, Type.Null()]),
    correctedStartedAt: Type.Union([DateTimeSchema, Type.Null()]),
    before: CoachServiceCorrectionEffectSchema,
    after: CoachServiceCorrectionEffectSchema,
    reason: ReasonSchema,
    consequences: Type.Array(Type.String({ minLength: 2, maxLength: 300 }), {
      minItems: 2,
      maxItems: 8,
      uniqueItems: true,
    }),
    correctionToken: Sha256Schema,
    canExecute: Type.Boolean(),
    blocker: Type.Union([Type.String({ minLength: 2, maxLength: 300 }), Type.Null()]),
    generatedAt: DateTimeSchema,
  },
  { additionalProperties: false },
);
export type CoachServiceCorrectionPreviewReadModel = Static<
  typeof CoachServiceCorrectionPreviewReadModelSchema
>;

export const CoachServiceCorrectionOperatorEffectSchema = Type.Object(
  {
    effectiveSeconds: Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER }),
    billedMinutes: Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER }),
    serviceFeeFen: Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER }),
    commissionFen: Type.Union([
      Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER }),
      Type.Null(),
    ]),
  },
  { additionalProperties: false },
);
export type CoachServiceCorrectionOperatorEffect = Static<
  typeof CoachServiceCorrectionOperatorEffectSchema
>;

export const CoachServiceCorrectionOperatorPreviewReadModelSchema = Type.Object(
  {
    serviceId: StableIdSchema,
    expectedServiceVersion: Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
    kind: CoachServiceCorrectionKindSchema,
    coachId: StableIdSchema,
    coachDisplayName: Type.String({ minLength: 1, maxLength: 80 }),
    tableNumber: Type.String({ minLength: 1, maxLength: 32 }),
    serviceTypeName: Type.String({ minLength: 1, maxLength: 80 }),
    serviceStatus: Type.Union([Type.Literal("ASSIGNED"), Type.Literal("ACTIVE")]),
    assignedAt: DateTimeSchema,
    originalStartedAt: Type.Union([DateTimeSchema, Type.Null()]),
    correctedStartedAt: Type.Union([DateTimeSchema, Type.Null()]),
    before: CoachServiceCorrectionOperatorEffectSchema,
    after: CoachServiceCorrectionOperatorEffectSchema,
    reason: ReasonSchema,
    consequences: Type.Array(Type.String({ minLength: 2, maxLength: 300 }), {
      minItems: 2,
      maxItems: 8,
      uniqueItems: true,
    }),
    correctionToken: Sha256Schema,
    canExecute: Type.Boolean(),
    blocker: Type.Union([Type.String({ minLength: 2, maxLength: 300 }), Type.Null()]),
    generatedAt: DateTimeSchema,
  },
  { additionalProperties: false },
);
export type CoachServiceCorrectionOperatorPreviewReadModel = Static<
  typeof CoachServiceCorrectionOperatorPreviewReadModelSchema
>;

export const ExecuteCoachServiceCorrectionCommandSchema = Type.Union([
  Type.Object(
    {
      kind: Type.Literal("MISASSIGN_CANCEL"),
      correctedStartedAt: Type.Null(),
      expectedServiceVersion: Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
      reason: ReasonSchema,
      previewGeneratedAt: DateTimeSchema,
      correctionToken: Sha256Schema,
      idempotencyKey: IdempotencyKeySchema,
    },
    { additionalProperties: false },
  ),
  Type.Object(
    {
      kind: Type.Literal("LATE_START_ADJUST"),
      correctedStartedAt: DateTimeSchema,
      expectedServiceVersion: Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
      reason: ReasonSchema,
      previewGeneratedAt: DateTimeSchema,
      correctionToken: Sha256Schema,
      idempotencyKey: IdempotencyKeySchema,
    },
    { additionalProperties: false },
  ),
]);
export type ExecuteCoachServiceCorrectionCommand = Static<
  typeof ExecuteCoachServiceCorrectionCommandSchema
>;

export const CoachServiceCorrectionNoticeStatusSchema = Type.Union([
  Type.Literal("PENDING"),
  Type.Literal("DELIVERED"),
  Type.Literal("READ"),
]);
export type CoachServiceCorrectionNoticeStatus = Static<
  typeof CoachServiceCorrectionNoticeStatusSchema
>;

export const CoachServiceCorrectionNoticeSchema = Type.Object(
  {
    id: StableIdSchema,
    correctionId: StableIdSchema,
    serviceId: StableIdSchema,
    coachId: StableIdSchema,
    kind: CoachServiceCorrectionKindSchema,
    title: Type.String({ minLength: 2, maxLength: 80 }),
    message: Type.String({ minLength: 2, maxLength: 500 }),
    status: CoachServiceCorrectionNoticeStatusSchema,
    version: Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
    createdAt: DateTimeSchema,
    deliveredAt: Type.Union([DateTimeSchema, Type.Null()]),
    readAt: Type.Union([DateTimeSchema, Type.Null()]),
  },
  { additionalProperties: false },
);
export type CoachServiceCorrectionNotice = Static<typeof CoachServiceCorrectionNoticeSchema>;

export const UpdateCoachServiceCorrectionNoticeRequestSchema = Type.Object(
  {
    status: Type.Union([Type.Literal("DELIVERED"), Type.Literal("READ")]),
    expectedVersion: Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
    idempotencyKey: IdempotencyKeySchema,
  },
  { additionalProperties: false },
);
export type UpdateCoachServiceCorrectionNoticeRequest = Static<
  typeof UpdateCoachServiceCorrectionNoticeRequestSchema
>;

export function isPreviewCoachServiceCorrectionRequest(
  value: unknown,
): value is PreviewCoachServiceCorrectionRequest {
  if (!Value.Check(PreviewCoachServiceCorrectionRequestSchema, value)) return false;
  const request = value as PreviewCoachServiceCorrectionRequest;
  if (request.reason !== request.reason.trim()) return false;
  return request.kind === "MISASSIGN_CANCEL"
    ? request.correctedStartedAt === null
    : request.correctedStartedAt !== null &&
        Number.isFinite(Date.parse(request.correctedStartedAt));
}

export function isExecuteCoachServiceCorrectionCommand(
  value: unknown,
): value is ExecuteCoachServiceCorrectionCommand {
  if (!Value.Check(ExecuteCoachServiceCorrectionCommandSchema, value)) return false;
  const request = value as ExecuteCoachServiceCorrectionCommand;
  if (request.reason !== request.reason.trim()) return false;
  if (!Number.isFinite(Date.parse(request.previewGeneratedAt))) return false;
  return request.kind === "MISASSIGN_CANCEL"
    ? request.correctedStartedAt === null
    : request.correctedStartedAt !== null &&
        Number.isFinite(Date.parse(request.correctedStartedAt));
}

export function isUpdateCoachServiceCorrectionNoticeRequest(
  value: unknown,
): value is UpdateCoachServiceCorrectionNoticeRequest {
  return Value.Check(UpdateCoachServiceCorrectionNoticeRequestSchema, value);
}

export const CoachServiceCorrectionResultSchema = Type.Object(
  {
    correctionId: StableIdSchema,
    correctionNumber: Type.String({ pattern: "^ZJ-[0-9]{8}-[0-9A-F]{12}$" }),
    kind: CoachServiceCorrectionKindSchema,
    serviceId: StableIdSchema,
    orderId: StableIdSchema,
    businessDate: Type.String({ pattern: "^[0-9]{4}-[0-9]{2}-[0-9]{2}$" }),
    before: CoachServiceCorrectionEffectSchema,
    after: CoachServiceCorrectionEffectSchema,
    correctedService: CoachServiceRecordSchema,
    notice: CoachServiceCorrectionNoticeSchema,
    reason: ReasonSchema,
    actorId: StableIdSchema,
    idempotencyKey: IdempotencyKeySchema,
    version: Type.Literal(1),
    correctedAt: DateTimeSchema,
  },
  { additionalProperties: false },
);
export type CoachServiceCorrectionResult = Static<typeof CoachServiceCorrectionResultSchema>;

export const CoachServiceCorrectionOperatorResultSchema = Type.Object(
  {
    correctionId: StableIdSchema,
    correctionNumber: Type.String({ pattern: "^ZJ-[0-9]{8}-[0-9A-F]{12}$" }),
    kind: CoachServiceCorrectionKindSchema,
    serviceId: StableIdSchema,
    orderId: StableIdSchema,
    businessDate: Type.String({ pattern: "^[0-9]{4}-[0-9]{2}-[0-9]{2}$" }),
    before: CoachServiceCorrectionOperatorEffectSchema,
    after: CoachServiceCorrectionOperatorEffectSchema,
    correctedService: Type.Object(
      {
        id: StableIdSchema,
        coachId: StableIdSchema,
        tableNumber: Type.String({ minLength: 1, maxLength: 32 }),
        status: Type.Union([Type.Literal("ACTIVE"), Type.Literal("ABNORMAL_TERMINATED")]),
        startedAt: Type.Union([DateTimeSchema, Type.Null()]),
        endedAt: Type.Union([DateTimeSchema, Type.Null()]),
        effectiveSeconds: Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER }),
        billedMinutes: Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER }),
        accruedServiceFeeFen: Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER }),
        version: Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
      },
      { additionalProperties: false },
    ),
    notice: CoachServiceCorrectionNoticeSchema,
    reason: ReasonSchema,
    actorId: StableIdSchema,
    idempotencyKey: IdempotencyKeySchema,
    version: Type.Literal(1),
    correctedAt: DateTimeSchema,
  },
  { additionalProperties: false },
);
export type CoachServiceCorrectionOperatorResult = Static<
  typeof CoachServiceCorrectionOperatorResultSchema
>;

function correctionEffectsHaveOneVisibility(
  before: CoachServiceCorrectionOperatorEffect,
  after: CoachServiceCorrectionOperatorEffect,
): boolean {
  return (before.commissionFen === null) === (after.commissionFen === null);
}

export function isCoachServiceCorrectionOperatorPreviewReadModel(
  value: unknown,
): value is CoachServiceCorrectionOperatorPreviewReadModel {
  if (!Value.Check(CoachServiceCorrectionOperatorPreviewReadModelSchema, value)) return false;
  const preview = value as CoachServiceCorrectionOperatorPreviewReadModel;
  if (!correctionEffectsHaveOneVisibility(preview.before, preview.after)) return false;
  if (preview.canExecute !== (preview.blocker === null)) return false;
  if (preview.kind === "MISASSIGN_CANCEL") {
    return (
      preview.correctedStartedAt === null &&
      preview.after.serviceFeeFen === 0 &&
      (preview.after.commissionFen === null || preview.after.commissionFen === 0)
    );
  }
  return (
    preview.serviceStatus === "ACTIVE" &&
    preview.originalStartedAt !== null &&
    preview.correctedStartedAt !== null &&
    Date.parse(preview.correctedStartedAt) > Date.parse(preview.originalStartedAt)
  );
}

export function isCoachServiceCorrectionOperatorResult(
  value: unknown,
): value is CoachServiceCorrectionOperatorResult {
  if (!Value.Check(CoachServiceCorrectionOperatorResultSchema, value)) return false;
  const result = value as CoachServiceCorrectionOperatorResult;
  if (!correctionEffectsHaveOneVisibility(result.before, result.after)) return false;
  if (
    result.correctedService.id !== result.serviceId ||
    result.notice.correctionId !== result.correctionId ||
    result.notice.serviceId !== result.serviceId ||
    result.notice.coachId !== result.correctedService.coachId ||
    result.notice.kind !== result.kind ||
    result.correctedService.effectiveSeconds !== 0 ||
    result.correctedService.billedMinutes !== 0 ||
    result.correctedService.accruedServiceFeeFen !== 0
  ) {
    return false;
  }
  if (result.kind === "MISASSIGN_CANCEL") {
    return (
      result.correctedService.status === "ABNORMAL_TERMINATED" &&
      result.correctedService.endedAt !== null &&
      result.after.effectiveSeconds === 0 &&
      result.after.billedMinutes === 0 &&
      result.after.serviceFeeFen === 0 &&
      (result.after.commissionFen === null || result.after.commissionFen === 0)
    );
  }
  return (
    result.correctedService.status === "ACTIVE" &&
    result.correctedService.endedAt === null &&
    result.correctedService.startedAt !== null
  );
}

export function coachServiceCorrectionOperatorPreview(
  preview: CoachServiceCorrectionPreviewReadModel,
  commissionVisible: boolean,
): CoachServiceCorrectionOperatorPreviewReadModel {
  return {
    ...preview,
    before: {
      ...preview.before,
      commissionFen: commissionVisible ? preview.before.commissionFen : null,
    },
    after: {
      ...preview.after,
      commissionFen: commissionVisible ? preview.after.commissionFen : null,
    },
  };
}

export function coachServiceCorrectionOperatorResult(
  result: CoachServiceCorrectionResult,
  commissionVisible: boolean,
): CoachServiceCorrectionOperatorResult {
  return {
    correctionId: result.correctionId,
    correctionNumber: result.correctionNumber,
    kind: result.kind,
    serviceId: result.serviceId,
    orderId: result.orderId,
    businessDate: result.businessDate,
    before: {
      ...result.before,
      commissionFen: commissionVisible ? result.before.commissionFen : null,
    },
    after: {
      ...result.after,
      commissionFen: commissionVisible ? result.after.commissionFen : null,
    },
    correctedService: {
      id: result.correctedService.id,
      coachId: result.correctedService.coachId,
      tableNumber: result.correctedService.tableNumber,
      status: result.correctedService.status === "ACTIVE" ? "ACTIVE" : "ABNORMAL_TERMINATED",
      startedAt: result.correctedService.startedAt,
      endedAt: result.correctedService.endedAt,
      effectiveSeconds: result.correctedService.effectiveSeconds ?? 0,
      billedMinutes: result.correctedService.billedMinutes,
      accruedServiceFeeFen: result.correctedService.accruedServiceFeeFen,
      version: result.correctedService.version,
    },
    notice: result.notice,
    reason: result.reason,
    actorId: result.actorId,
    idempotencyKey: result.idempotencyKey,
    version: result.version,
    correctedAt: result.correctedAt,
  };
}

export function isCoachServiceCorrectionPreviewReadModel(
  value: unknown,
): value is CoachServiceCorrectionPreviewReadModel {
  if (!Value.Check(CoachServiceCorrectionPreviewReadModelSchema, value)) return false;
  const preview = value as CoachServiceCorrectionPreviewReadModel;
  if (preview.canExecute !== (preview.blocker === null)) return false;
  if (preview.kind === "MISASSIGN_CANCEL") {
    return (
      preview.correctedStartedAt === null &&
      preview.after.serviceFeeFen === 0 &&
      preview.after.commissionFen === 0
    );
  }
  return (
    preview.serviceStatus === "ACTIVE" &&
    preview.originalStartedAt !== null &&
    preview.correctedStartedAt !== null &&
    Date.parse(preview.correctedStartedAt) > Date.parse(preview.originalStartedAt)
  );
}

export function isCoachServiceCorrectionResult(
  value: unknown,
): value is CoachServiceCorrectionResult {
  if (!Value.Check(CoachServiceCorrectionResultSchema, value)) return false;
  const result = value as CoachServiceCorrectionResult;
  if (
    result.correctedService.id !== result.serviceId ||
    result.correctedService.orderId !== result.orderId ||
    result.correctedService.businessDate !== result.businessDate ||
    result.notice.correctionId !== result.correctionId ||
    result.notice.serviceId !== result.serviceId ||
    result.notice.coachId !== result.correctedService.coachId ||
    result.notice.kind !== result.kind
  ) {
    return false;
  }
  const correctedServiceHasNoAccruedCharge =
    result.correctedService.effectiveSeconds === 0 &&
    result.correctedService.billedMinutes === 0 &&
    result.correctedService.accruedServiceFeeFen === 0 &&
    result.correctedService.commissionBaseFen === 0 &&
    result.correctedService.commissionFen === 0;
  if (!correctedServiceHasNoAccruedCharge) return false;
  if (result.kind === "MISASSIGN_CANCEL") {
    return (
      result.correctedService.status === "ABNORMAL_TERMINATED" &&
      result.correctedService.endedAt !== null &&
      result.after.effectiveSeconds === 0 &&
      result.after.billedMinutes === 0 &&
      result.after.serviceFeeFen === 0 &&
      result.after.commissionFen === 0
    );
  }
  return (
    result.correctedService.status === "ACTIVE" &&
    result.correctedService.endedAt === null &&
    result.correctedService.startedAt !== null &&
    result.correctedService.startedAt !== result.correctedService.assignedAt
  );
}
